// src/pages/pmc/PmcWorkflowStageMonitorPane.js
import { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import toast from "react-hot-toast";
import dayjs from "dayjs";

/* --------------------- small helpers --------------------- */
const cx = (...xs) => xs.filter(Boolean).join(" ");

function Badge({ color = "gray", children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-700",
    yellow: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-gray-100 text-gray-700",
    blue: "bg-sky-100 text-sky-700",
    purple: "bg-purple-100 text-purple-700",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold",
        map[color] || map.gray
      )}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value, note }) {
  return (
    <div className="rounded-2xl bg-white shadow-sm border border-emerald-100 px-4 py-3 flex flex-col justify-between">
      <div className="text-[11px] text-slate-500 uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-1 flex items-end justify-between gap-2">
        <div className="text-2xl font-extrabold text-emerald-800">{value}</div>
        {note && (
          <div className="text-[10px] text-slate-400 text-right max-w-[150px] leading-snug">
            {note}
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressBar({ value }) {
  const pct = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div className="w-full h-2.5 rounded-full bg-slate-200/70 overflow-hidden">
      <div
        className={cx(
          "h-full rounded-full transition-all duration-300",
          pct < 25
            ? "bg-amber-400"
            : pct < 50
            ? "bg-yellow-400"
            : pct < 75
            ? "bg-sky-500"
            : "bg-emerald-500"
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

const Label = ({ children }) => (
  <div className="text-[11px] font-semibold text-slate-600">{children}</div>
);

const tsToDate = (ts) => {
  try {
    if (!ts) return null;
    if (typeof ts.toDate === "function") return ts.toDate();
    if (ts.seconds) return new Date(ts.seconds * 1000);
    return new Date(ts);
  } catch {
    return null;
  }
};

/* --------------------- CSV helper --------------------- */
function downloadCsv(filename, rows) {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const csv =
    rows.map((row) => row.map(esc).join(",")).join("\n") + "\n";

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* --------------------- main component --------------------- */

export default function PmcWorkflowStageMonitorPane() {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [vendors, setVendors] = useState([]); // 👈 new
  const [loading, setLoading] = useState(true);
  const [districtFilter, setDistrictFilter] = useState("all");
  const [q, setQ] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [pSnap, tSnap, vSnap] = await Promise.all([
        getDocs(collection(db, "wf_projects")),
        getDocs(collection(db, "wf_project_tasks")),
        getDocs(collection(db, "work_vendors")), // 👈 vendors bhi
      ]);

      const pList = [];
      pSnap.forEach((d) => pList.push({ id: d.id, ...d.data() }));

      const tList = [];
      tSnap.forEach((d) => tList.push({ id: d.id, ...d.data() }));

      const vList = [];
      vSnap.forEach((d) => vList.push({ id: d.id, ...d.data() }));

      setProjects(pList);
      setTasks(tList);
      setVendors(vList);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load workflow projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* ------------- project + task aggregation ------------- */

  const projectViews = useMemo(() => {
    const byProject = {};
    tasks.forEach((t) => {
      if (!t.projectId) return;
      if (!byProject[t.projectId]) byProject[t.projectId] = [];
      byProject[t.projectId].push(t);
    });

    return projects.map((p) => {
      const list = byProject[p.id] || [];
      const total = list.length;
      const completed = list.filter(
        (t) => (t.status || "").toUpperCase() === "COMPLETED"
      ).length;
      const pending = list.filter(
        (t) => (t.status || "").toUpperCase() !== "COMPLETED"
      ).length;
      const progress = total === 0 ? 0 : Math.round((completed / total) * 100);

      let stage = "Not Started";
      if (total === 0 || progress === 0) stage = "Not Started";
      else if (progress === 100) stage = "Completed";
      else if (progress >= 75) stage = "Near Completion";
      else stage = "In Progress";

      const latestTs =
        list
          .map((t) => t.updatedAt || t.completedAt || t.assignedAt)
          .filter(Boolean)
          .sort((a, b) => {
            const da = tsToDate(a)?.getTime() || 0;
            const db = tsToDate(b)?.getTime() || 0;
            return db - da;
          })[0] || null;

      return {
        ...p,
        _tasks: list,
        _taskStats: { total, completed, pending },
        stage,
        progress,
        lastUpdated: latestTs,
      };
    });
  }, [projects, tasks]);

  const districtOptions = useMemo(() => {
    const set = new Set(
      projectViews
        .map((p) => (p.district || "").trim())
        .filter((x) => x.length)
    );
    return ["all", ...Array.from(set)];
  }, [projectViews]);

  const filtered = useMemo(() => {
    let list = [...projectViews];
    if (districtFilter !== "all") {
      list = list.filter((p) => (p.district || "") === districtFilter);
    }
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((p) => {
        const hay = [
          p.pacsName,
          p.title,
          p.locationVillage,
          p.taluka,
          p.district,
          p.id,
        ]
          .map((x) => String(x || "").toLowerCase())
          .join("|");
        return hay.includes(needle);
      });
    }
    list.sort((a, b) => {
      const da =
        tsToDate(a.lastUpdated)?.getTime() ||
        tsToDate(a.updatedAt)?.getTime() ||
        0;
      const db =
        tsToDate(b.lastUpdated)?.getTime() ||
        tsToDate(b.updatedAt)?.getTime() ||
        0;
      return db - da;
    });
    return list;
  }, [projectViews, districtFilter, q]);

  const totals = useMemo(() => {
    const total = filtered.length;
    const notStarted = filtered.filter((p) => p.stage === "Not Started")
      .length;
    const completed = filtered.filter((p) => p.stage === "Completed").length;
    const nearCompletion = filtered.filter(
      (p) => p.stage === "Near Completion"
    ).length;
    const inProgress = total - notStarted - completed - nearCompletion;
    return { total, notStarted, inProgress, nearCompletion, completed };
  }, [filtered]);

  const stageColor = (stage) => {
    if (stage === "Completed") return "green";
    if (stage === "Near Completion") return "purple";
    if (stage === "In Progress") return "blue";
    return "gray";
  };

  /* ----------------- CSV generators (same as pehle) ----------------- */
  const exportProjectsCsv = () => {
    if (!filtered.length) {
      toast("No projects to export");
      return;
    }
    const header = [
      "PACS",
      "Project Title",
      "District",
      "Taluka",
      "Village",
      "Stage",
      "Progress (%)",
      "Last Updated",
      "Project ID",
    ];
    const rows = filtered.map((p) => {
      const d =
        tsToDate(p.lastUpdated) ||
        tsToDate(p.updatedAt) ||
        tsToDate(p.createdAt);
      return [
        p.pacsName || "",
        p.title || "",
        p.district || "",
        p.taluka || "",
        p.locationVillage || "",
        p.stage || "",
        p.progress || 0,
        d ? dayjs(d).format("YYYY-MM-DD") : "",
        p.id,
      ];
    });
    downloadCsv("workflow_projects.csv", [header, ...rows]);
  };

  const exportDistrictSummaryCsv = () => {
    if (!filtered.length) {
      toast("No projects to export");
      return;
    }
    const byDistrict = {};
    filtered.forEach((p) => {
      const d = p.district || "Unknown";
      if (!byDistrict[d]) byDistrict[d] = { count: 0, progressSum: 0 };
      byDistrict[d].count += 1;
      byDistrict[d].progressSum += Number(p.progress || 0);
    });
    const header = ["District", "Projects", "Avg Progress (%)"];
    const rows = Object.entries(byDistrict).map(([d, v]) => [
      d,
      v.count,
      v.count ? Math.round(v.progressSum / v.count) : 0,
    ]);
    downloadCsv("workflow_district_summary.csv", [header, ...rows]);
  };

  const exportStageSummaryCsv = () => {
    if (!filtered.length) {
      toast("No projects to export");
      return;
    }
    const byStage = {};
    filtered.forEach((p) => {
      const s = p.stage || "Not Started";
      if (!byStage[s]) byStage[s] = 0;
      byStage[s] += 1;
    });
    const header = ["Stage", "Projects"];
    const rows = Object.entries(byStage).map(([s, c]) => [s, c]);
    downloadCsv("workflow_stage_summary.csv", [header, ...rows]);
  };

  /* --------------------- render --------------------- */

  return (
    <div className="space-y-5">
      {/* Header + Stats */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-emerald-800">
            Workflow Stage Monitor
          </div>
          <div className="text-[11px] text-slate-600">
            Project Register view – wf_projects + wf_project_tasks ka live
            status.
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid lg:grid-cols-5 sm:grid-cols-3 grid-cols-2 gap-3">
        <StatCard
          label="Total Workflow Projects"
          value={totals.total}
          note="All projects moved into workflow pipeline."
        />
        <StatCard label="Not Started" value={totals.notStarted} />
        <StatCard label="In Progress" value={totals.inProgress} />
        <StatCard label="Near Completion" value={totals.nearCompletion} />
        <StatCard label="Completed" value={totals.completed} />
      </div>

      {/* Filters + CSV buttons */}
      <div className="rounded-2xl bg-white shadow-sm border border-slate-300 px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
          <div className="w-full md:w-48">
            <Label>District</Label>
            <select
              value={districtFilter}
              onChange={(e) => setDistrictFilter(e.target.value)}
              className="mt-1 w-full px-3 py-2 border rounded-xl text-sm bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              {districtOptions.map((d) => (
                <option key={d} value={d}>
                  {d === "all" ? "All" : d}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <Label>Search</Label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="PACS / Project / Village / ID…"
              className="mt-1 w-full px-3 py-2 border rounded-xl text-sm bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={exportProjectsCsv}
            className="px-3 py-1.5 rounded-xl border border-slate-400 text-[11px] font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
          >
            Export Projects CSV
          </button>
          <button
            type="button"
            onClick={exportDistrictSummaryCsv}
            className="px-3 py-1.5 rounded-xl border border-slate-400 text-[11px] font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
          >
            District Summary CSV
          </button>
          <button
            type="button"
            onClick={exportStageSummaryCsv}
            className="px-3 py-1.5 rounded-xl border border-slate-400 text-[11px] font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
          >
            Stage Summary CSV
          </button>
        </div>
      </div>

      {/* Project Register table */}
      <div className="rounded-2xl bg-white border border-slate-400 shadow-sm overflow-hidden">
        <div className="px-4 pt-3 pb-2 flex items-center justify-between text-[11px] text-slate-600 border-b border-slate-400/80">
          <div className="font-semibold">Project Register (Filtered View)</div>
          <div>{filtered.length} record(s) found</div>
        </div>

        {loading ? (
          <div className="px-4 py-6 text-xs text-slate-500">
            Loading workflow projects…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-6 text-xs text-slate-500">
            No workflow projects match current filters.
          </div>
        ) : (
          <div>
            {/* header row */}
            <div className="hidden md:grid grid-cols-[50px,minmax(0,2.4fr),minmax(0,1.4fr),minmax(0,1.4fr),minmax(0,1.6fr),minmax(0,1.2fr),80px] bg-slate-50 text-[11px] font-semibold text-slate-700 border-b border-slate-400">
              <div className="px-3 py-2 border-r border-slate-300">#</div>
              <div className="px-3 py-2 border-r border-slate-300">
                PACS / Project
              </div>
              <div className="px-3 py-2 border-r border-slate-300">
                District
              </div>
              <div className="px-3 py-2 border-r border-slate-300">Stage</div>
              <div className="px-3 py-2 border-r border-slate-300">
                Progress
              </div>
              <div className="px-3 py-2 border-r border-slate-300">
                Last Updated
              </div>
              <div className="px-3 py-2 text-right">Action</div>
            </div>

            {filtered.map((p, idx) => {
              const isOpen = expandedId === p.id;
              const lastUpdateDate =
                tsToDate(p.lastUpdated) ||
                tsToDate(p.updatedAt) ||
                tsToDate(p.createdAt);

              const projectTasks = [...(p._tasks || [])].sort((a, b) => {
                const da =
                  tsToDate(a.updatedAt || a.completedAt)?.getTime() || 0;
                const db =
                  tsToDate(b.updatedAt || b.completedAt)?.getTime() || 0;
                return db - da;
              });

              return (
                <div key={p.id} className="border-b border-slate-300">
                  {/* main row */}
                  <div className="md:grid md:grid-cols-[50px,minmax(0,2.4fr),minmax(0,1.4fr),minmax(0,1.4fr),minmax(0,1.6fr),minmax(0,1.2fr),80px] flex flex-col bg-white">
                    <div className="px-3 py-2 text-[11px] text-slate-500 flex items-center border-r border-slate-300 md:border-b-0 border-b border-slate-300">
                      {idx + 1}
                    </div>
                    <div className="px-3 py-2 space-y-0.5 border-r border-slate-300 md:border-b-0 border-b border-slate-300">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 text-xs">
                          {p.pacsName || "—"}
                        </span>
                        {p.capacityMt && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                            {p.capacityMt} MT
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-600 line-clamp-1">
                        {p.title || "—"}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono">
                        ID: {p.id}
                      </div>
                    </div>
                    <div className="px-3 py-2 text-[11px] text-slate-800 border-r border-slate-300 md:border-b-0 border-b border-slate-300">
                      <div>{p.district || "—"}</div>
                      <div className="text-slate-500">
                        {p.taluka || "—"}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {p.locationVillage || "—"}
                      </div>
                    </div>
                    <div className="px-3 py-2 flex items-center border-r border-slate-300 md:border-b-0 border-b border-slate-300">
                      <Badge color={stageColor(p.stage)}>{p.stage}</Badge>
                    </div>
                    <div className="px-3 py-2 flex items-center gap-3 border-r border-slate-300 md:border-b-0 border-b border-slate-300">
                      <div className="flex-1">
                        <ProgressBar value={p.progress || 0} />
                      </div>
                      <div className="w-10 text-right text-[11px] font-semibold text-slate-700">
                        {p.progress || 0}%
                      </div>
                    </div>
                    <div className="px-3 py-2 text-[11px] text-slate-700 border-r border-slate-300 md:border-b-0 border-b border-slate-300">
                      {lastUpdateDate
                        ? dayjs(lastUpdateDate).format("DD/MM/YYYY")
                        : "—"}
                    </div>
                    <div className="px-3 py-2 flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedId(isOpen ? null : p.id)
                        }
                        className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900"
                      >
                        {isOpen ? "Close" : "View »"}
                      </button>
                    </div>
                  </div>

                  {/* detail row */}
                  {isOpen && (
                    <div className="bg-emerald-50/60 px-4 pb-4 pt-2">
                      <div className="rounded-xl bg-emerald-50 border border-slate-400 overflow-hidden">
                        {/* detail header */}
                        <div className="px-4 py-2.5 bg-emerald-100 border-b border-slate-400 flex justify-between items-center">
                          <div>
                            <div className="text-xs font-semibold text-emerald-900">
                              {p.pacsName || "—"}
                            </div>
                            <div className="text-[11px] text-emerald-800">
                              {p.title || "—"}
                            </div>
                            <div className="text-[10px] text-emerald-700">
                              {p.locationVillage || "—"},{" "}
                              {p.taluka || "—"},{" "}
                              {p.district || "—"}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="text-[11px] text-emerald-800 underline"
                            onClick={() => setExpandedId(null)}
                          >
                            Close
                          </button>
                        </div>

                        {/* tasks table */}
                        <div className="overflow-x-auto">
                          <table
                            className="min-w-full text-[11px]"
                            style={{ borderCollapse: "collapse" }}
                          >
                            <thead>
                              <tr className="bg-emerald-50 text-emerald-900 border-b border-slate-400">
                                <th className="px-3 py-2 text-left border border-black w-28">
                                  Type
                                </th>
                                <th className="px-3 py-2 text-left border border-black w-60">
                                  Vendor
                                </th>
                                <th className="px-3 py-2 text-left border border-black w-32">
                                  Status
                                </th>
                                <th className="px-3 py-2 text-left border border-black w-64">
                                  Files
                                </th>
                                <th className="px-3 py-2 text-left border border-black w-72">
                                  Planned / Timeline
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {projectTasks.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={5}
                                    className="px-3 py-3 text-[11px] text-slate-500 text-center border border-black"
                                  >
                                    No workflow tasks created for this project
                                    yet.
                                  </td>
                                </tr>
                              ) : (
                                projectTasks.map((t, idxT) => {
                                  /* vendor from work_vendors */
                                  const vendor = vendors.find(
                                    (v) => v.id === t.vendorId
                                  );
                                  const vendorName =
                                    vendor?.name ||
                                    vendor?.vendorName ||
                                    vendor?.fullName;
                                  const vendorFirm =
                                    vendor?.firmName ||
                                    vendor?.companyName ||
                                    vendor?.firm;

                                  const vendorDisplay =
                                    vendorName || vendorFirm || "—";

                                  const status = (t.status || "ASSIGNED").toUpperCase();
                                  const statusColor =
                                    status === "COMPLETED"
                                      ? "green"
                                      : status === "ASSIGNED"
                                      ? "yellow"
                                      : "blue";

                                  const files =
                                    Array.isArray(t.files) ? t.files : [];

                                  const assignedAt = tsToDate(t.assignedAt);
                                  const completedAt = tsToDate(t.completedAt);

                                  const plannedDisplay =
                                    t.plannedDateTime ||
                                    (tsToDate(t.plannedAt)
                                      ? dayjs(tsToDate(t.plannedAt)).format(
                                          "DD/MM/YYYY, hh:mm A"
                                        )
                                      : "—");

                                  const typeLabel =
                                    t.type ||
                                    t.taskType ||
                                    t.stageLabel ||
                                    t.stage ||
                                    "Task";

                                  return (
                                    <tr
                                      key={t.id || idxT}
                                      className={cx(
                                        "hover:bg-emerald-50",
                                        idxT % 2 === 0
                                          ? "bg-white"
                                          : "bg-emerald-50/60"
                                      )}
                                    >
                                      <td className="px-3 py-1 border border-black align-top">
                                        <span className="font-semibold text-emerald-800">
                                          {typeLabel}
                                        </span>
                                      </td>
                                      <td className="px-3 py-1 border border-black align-top">
                                        <div className="text-[11px] font-medium text-slate-800">
                                          {vendorDisplay}
                                        </div>
                                        {vendorName && vendorFirm && (
                                          <div className="text-[10px] text-slate-600">
                                            {vendorFirm}
                                          </div>
                                        )}
                                      </td>
                                      <td className="px-3 py-1 border border-black align-top">
                                        <Badge color={statusColor}>
                                          {status}
                                        </Badge>
                                      </td>
                                      <td className="px-3 py-1 border border-black align-top">
                                        {files.length ? (
                                          <div className="text-[11px] space-y-1">
                                            <div>{files.length} file(s)</div>
                                            {files.slice(0, 3).map((f, i) => (
                                              <div key={i}>
                                                <a
                                                  href={f.url}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="text-emerald-700 underline"
                                                >
                                                  {f.name ||
                                                    f.filename ||
                                                    `File ${i + 1}`}
                                                </a>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <span className="text-[11px] text-gray-400">
                                            No files
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-1 border border-black align-top">
                                        <div className="text-[11px]">
                                          <b>Assigned:</b>{" "}
                                          {assignedAt
                                            ? dayjs(assignedAt).format(
                                                "DD/MM/YYYY, hh:mm A"
                                              )
                                            : "—"}
                                        </div>
                                        <div className="text-[11px]">
                                          <b>Planned:</b> {plannedDisplay}
                                        </div>
                                        <div className="text-[11px]">
                                          <b>Completed:</b>{" "}
                                          {completedAt
                                            ? dayjs(completedAt).format(
                                                "DD/MM/YYYY, hh:mm A"
                                              )
                                            : "—"}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
